import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { HeaderWrapper } from '~/styles/layoutElements';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  itemLabel?: string;
}

function generatePageNumbers(
  currentPage: number,
  totalPages: number,
): (number | 'ellipsis')[] {
  if (totalPages <= 6) {
    // If 6 or fewer pages, show all
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];

  // Always show first page
  pages.push(1);

  if (currentPage <= 3) {
    // Near the beginning: show 1, 2, 3, (4 if current is 3), ..., last
    for (let i = 2; i <= Math.min(currentPage + 1, 4); i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  } else if (currentPage >= totalPages - 2) {
    // Near the end: mirror the beginning pattern
    // Last page: show 1, ..., last-1, last
    // Second to last: show 1, ..., last-2, last-1, last
    // Third to last: show 1, ..., last-3, last-2, last-1, last
    pages.push('ellipsis');
    const startPage = Math.max(totalPages - (totalPages - currentPage + 1), 2);
    for (let i = startPage; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    // In the middle: show 1, ..., current-1, current, current+1, ..., last
    pages.push('ellipsis');
    pages.push(currentPage - 1);
    pages.push(currentPage);
    pages.push(currentPage + 1);
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return pages;
}

export function Pagination({
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  totalItems,
  onPageChange,
  // Translation Needed
  itemLabel = 'items',
}: PaginationProps) {
  const pageNumbers =
    totalPages > 1 ? generatePageNumbers(currentPage, totalPages) : [];

  return (
    <PaginationContainer>
      {/* Translation Needed */}
      <PageInfo>
        Viewing {startIndex + 1} - {Math.min(endIndex, totalItems)} of{' '}
        {totalItems} {itemLabel}
      </PageInfo>
      {totalPages > 1 && (
        <PaginationButtons>
          {currentPage > 1 && (
            <ArrowButton
              onClick={() => onPageChange(currentPage - 1)}
              // Translation Needed
              aria-label="Previous page"
              $side="left"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </ArrowButton>
          )}
          {pageNumbers.map((page, index) =>
            page === 'ellipsis' ? (
              <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>
            ) : (
              <PageButton
                key={page}
                $active={page === currentPage}
                onClick={() => onPageChange(page)}
              >
                {page}
              </PageButton>
            ),
          )}
          {currentPage < totalPages && (
            <ArrowButton
              onClick={() => onPageChange(currentPage + 1)}
              // Translation Needed
              aria-label="Next page"
              $side="right"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </ArrowButton>
          )}
        </PaginationButtons>
      )}
    </PaginationContainer>
  );
}

const PaginationContainer = styled(HeaderWrapper)`
  margin-top: 40px;
`;

const PageInfo = styled.div`
  color: var(--grey-500);
  font-family: var(--raleway);
  font-size: 13px;
  letter-spacing: -0.2px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const Ellipsis = styled.span`
  align-items: center;
  color: var(--grey-500);
  display: flex;
  font-family: var(--open-sans);
  font-size: 13px;
  padding: 6px 4px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 50%;
  color: var(--wbs-red);
  cursor: pointer;
  display: flex;
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 300;
  height: 32px;
  justify-content: center;
  transition:
    color 0.2s,
    background 0.2s;
  width: 32px;

  ${(props) =>
    props.$active &&
    `
      color: var(--grey-700);
      background: none;
    `}

  &:hover {
    background: var(--grey-200);
    color: var(--wbs-red);
  }
`;

const ArrowButton = styled.button<{ $side: 'left' | 'right' }>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 50%;
  color: var(--wbs-red);
  cursor: pointer;
  display: flex;
  font-family: var(--open-sans);
  font-size: 13px;
  font-weight: 600;
  height: 32px;
  justify-content: center;
  transition:
    color 0.2s,
    background 0.2s;
  width: 32px;

  &:hover {
    background: var(--grey-200);
    color: var(--wbs-red);
  }
`;
